// ============================================================================
// SHARED / GLOBAL CONCEPTS (Phase L-1 — the main current of the river)
//
// specialtyId = null. Cross-specialty evidence patterns every specialty reuses.
// This is NOT a medical ontology — it is a ReferralGate OPERATIONAL evidence
// library. Concepts define how language/documents point at meaning; they NEVER
// hold patient values.
//
// CLASSIFICATION (L-1 contract — docs/L-1-shared-concept-library-contract.md):
//   • LIVE_LINKABLE — auto-links now when matched. EXACTLY 3 findings:
//       abnormal_lab (new), abnormal_imaging (promoted), prior_workup (promoted).
//       They ride the EXISTING FINDING recognition group → zero auto-link logic
//       change; their patterns live in `recognitionPatterns` below and their
//       conceptIds are appended to RECOGNITION_GROUPS.FINDING in index.js.
//   • DATA_ONLY — present in the library but NOT recognition-reachable (no
//       pattern, no group membership) → never auto-links / surfaces yet. They are
//       the foundation specialty modules will reuse once the deferred
//       DATE / MEDICATION_EXPOSURE / WORKUP / SYMPTOM / DOCUMENT recognition
//       groups + small linker extensions ship (a later slice). Every DATA_ONLY
//       red flag is fully boundaried (positive/negative/uncertain/excluded) so
//       promotion is a one-line group/pattern add, not a rewrite.
//   • DELAYED_RISKY — NOT added (documented below). Broad single words
//       (pain, bleeding, infection, mass, swelling, fatigue, trauma), narrative
//       burden/QoL concepts, abnormal_test_result, allergy_history, and the
//       prior_report/latest_report/last_reviewed_date qualifiers. Reason: findings
//       get NO match-time negation (only red flags suppress), so broad terms would
//       false-positive unsafely; they are admissible later only as strictly
//       bounded red_flags or once a SYMPTOM group + narrow phrasing exist.
//
// L-1 adds NO new LIVE red flags (very conservative). Existing live red flags
// (active_gi_bleeding, chest_pain) are unchanged.
//
// PURE / Never-PHI. Concept records are pure JSON data; recognition patterns are
// a separate code-level RegExp table (flags 'i' only → stateless).
// ============================================================================

import { makeConcept, CONCEPT_TYPES } from './conceptBuilder.js';

export const concepts = [
  // ══════════════════════════════════════════════════════════════════════════
  // DOCUMENT CONCEPTS
  // ══════════════════════════════════════════════════════════════════════════
  // ── Existing (moved verbatim) — from DOC_TYPE_LABEL_HINTS ───────────────────
  makeConcept({
    conceptId: 'lab_result', conceptType: CONCEPT_TYPES.DOCUMENT,
    canonicalLabel: 'Lab result',
    description: 'A laboratory result document (CBC, metabolic panel, blood work).',
    aliases: ['lab result', 'laboratory result', 'labs', 'blood test', 'blood panel', 'blood work'],
    abbreviations: ['BMP', 'CMP', 'HbA1c'],
    preferredDocumentTypes: ['LAB_RESULT'],
  }),
  makeConcept({
    conceptId: 'imaging_report', conceptType: CONCEPT_TYPES.DOCUMENT,
    canonicalLabel: 'Imaging report',
    description: 'A radiology / imaging report (MRI, CT, X-ray, ultrasound).',
    aliases: ['imaging report', 'radiology report', 'MRI', 'CT', 'x-ray', 'ultrasound', 'radiograph'],
    abbreviations: ['MRI', 'CT'],
    preferredDocumentTypes: ['IMAGING_REPORT'],
  }),
  makeConcept({
    conceptId: 'referral_letter', conceptType: CONCEPT_TYPES.DOCUMENT,
    canonicalLabel: 'Referral letter',
    description: 'A referral / referring-provider letter.',
    aliases: ['referral letter', 'referral', 'referring letter'],
    preferredDocumentTypes: ['REFERRAL_LETTER'],
  }),
  makeConcept({
    conceptId: 'clinical_note', conceptType: CONCEPT_TYPES.DOCUMENT,
    canonicalLabel: 'Clinical note',
    description: 'A clinical / progress note.',
    aliases: ['clinical note', 'progress note', 'office note', 'note'],
    preferredDocumentTypes: ['CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'medication_list', conceptType: CONCEPT_TYPES.DOCUMENT,
    canonicalLabel: 'Medication list',
    description: 'A current medication / drug list.',
    aliases: ['medication list', 'med list', 'medications', 'drug list', 'meds'],
    preferredDocumentTypes: ['MEDICATION_LIST'],
  }),
  makeConcept({
    conceptId: 'discharge_summary', conceptType: CONCEPT_TYPES.DOCUMENT,
    canonicalLabel: 'Discharge summary',
    description: 'A hospital discharge summary.',
    aliases: ['discharge summary', 'discharge'],
    preferredDocumentTypes: ['DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'consult_note', conceptType: CONCEPT_TYPES.DOCUMENT,
    canonicalLabel: 'Consult note',
    description: 'A specialist consultation note.',
    aliases: ['consult note', 'consultation note', 'consult'],
    preferredDocumentTypes: ['CONSULT_NOTE'],
  }),
  makeConcept({
    conceptId: 'complete_blood_count', conceptType: CONCEPT_TYPES.DOCUMENT,
    canonicalLabel: 'Complete blood count',
    description: 'A complete blood count (CBC / FBC) lab document.',
    aliases: ['CBC', 'complete blood count', 'FBC', 'full blood count'],
    abbreviations: ['CBC', 'FBC'],
    preferredDocumentTypes: ['LAB_RESULT'],
  }),

  // ── DATA_ONLY documents (L-1) — map to canonical enum; no enum expansion ────
  // pathology / operative / procedure reports have no canonical doc type; they
  // map to OTHER / CLINICAL_NOTE and stay DATA_ONLY until a DOCUMENT recognition
  // group (consulted by linkAttachment) ships. (L-1 contract Part F.)
  makeConcept({
    conceptId: 'pathology_report', conceptType: CONCEPT_TYPES.DOCUMENT,
    canonicalLabel: 'Pathology report',
    description: 'A pathology / histology / biopsy report (maps to OTHER).',
    aliases: ['pathology report', 'pathology', 'biopsy report', 'histology report', 'histopathology'],
    preferredDocumentTypes: ['OTHER'],
    acceptableDocumentTypes: ['CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'operative_report', conceptType: CONCEPT_TYPES.DOCUMENT,
    canonicalLabel: 'Operative report',
    description: 'An operative / surgical note (maps to CLINICAL_NOTE).',
    aliases: ['operative report', 'op note', 'operative note', 'surgical report'],
    preferredDocumentTypes: ['CLINICAL_NOTE'],
    acceptableDocumentTypes: ['OTHER'],
  }),
  makeConcept({
    conceptId: 'procedure_report', conceptType: CONCEPT_TYPES.DOCUMENT,
    canonicalLabel: 'Procedure report',
    description: 'A procedure report (endoscopy, EMG, etc.; maps to OTHER).',
    aliases: ['procedure report', 'procedure note'],
    preferredDocumentTypes: ['OTHER'],
    acceptableDocumentTypes: ['CLINICAL_NOTE'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // MEASUREMENT / SEVERITY CONCEPTS
  // ══════════════════════════════════════════════════════════════════════════
  // ── Existing (moved verbatim) ──────────────────────────────────────────────
  makeConcept({
    conceptId: 'creatinine', conceptType: CONCEPT_TYPES.MEASUREMENT,
    canonicalLabel: 'Creatinine',
    description: 'Serum creatinine from a metabolic panel or lab result.',
    aliases: ['creatinine', 'serum creatinine', 'Cr'],
    abbreviations: ['Cr'],
    preferredUnit: 'mg/dL',
    allowedUnits: ['mg/dL', 'umol/L'],
    valuePattern: 'numeric_near_label',
    requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'],
    acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  // ── L-8 shared-labs promotion: hba1c + tsh moved here from cardiology (were
  //    cardiology DATA_ONLY/inert). Universal cross-specialty labs (diabetes /
  //    thyroid / cardiometabolic / renal), like creatinine → one source of truth.
  //    Aliases/units preserved; now LIVE (pattern below + LAB group in index.js).
  makeConcept({
    conceptId: 'hba1c', conceptType: CONCEPT_TYPES.MEASUREMENT,
    canonicalLabel: 'HbA1c',
    description: 'Glycated hemoglobin (HbA1c) — glycaemic control / diabetes monitoring.',
    aliases: ['HbA1c', 'hemoglobin A1c', 'glycated hemoglobin'],
    abbreviations: ['HbA1c'],
    preferredUnit: '%',
    allowedUnits: ['%', 'mmol/mol'],
    valuePattern: 'numeric_near_label',
    requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'],
    acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'tsh', conceptType: CONCEPT_TYPES.MEASUREMENT,
    canonicalLabel: 'TSH',
    description: 'Thyroid stimulating hormone (TSH) — thyroid function.',
    aliases: ['TSH', 'thyroid stimulating hormone'],
    abbreviations: ['TSH'],
    preferredUnit: 'mIU/L',
    allowedUnits: ['mIU/L'],
    valuePattern: 'numeric_near_label',
    requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'],
    acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  // ── L-10 shared-labs promotion: CRP + ESR moved here from gastroenterology (were
  //    GI DATA_ONLY/inert). Universal acute-phase inflammatory markers (rheumatology /
  //    GI / infection). Aliases/units preserved; now LIVE (patterns below + LAB group).
  makeConcept({
    conceptId: 'c_reactive_protein', conceptType: CONCEPT_TYPES.MEASUREMENT,
    canonicalLabel: 'C-reactive protein',
    description: 'C-reactive protein (CRP) — acute-phase inflammatory marker (cross-specialty).',
    aliases: ['C-reactive protein', 'C reactive protein', 'CRP'],
    abbreviations: ['CRP'],
    preferredUnit: 'mg/L',
    allowedUnits: ['mg/L'],
    valuePattern: 'numeric_near_label',
    requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'],
    acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'erythrocyte_sedimentation_rate', conceptType: CONCEPT_TYPES.MEASUREMENT,
    canonicalLabel: 'ESR',
    description: 'Erythrocyte sedimentation rate (ESR) — acute-phase inflammatory marker (cross-specialty).',
    aliases: ['ESR', 'erythrocyte sedimentation rate'],
    abbreviations: ['ESR'],
    preferredUnit: 'mm/hr',
    allowedUnits: ['mm/hr'],
    valuePattern: 'numeric_near_label',
    requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'],
    acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'symptom_duration', conceptType: CONCEPT_TYPES.MEASUREMENT,
    canonicalLabel: 'Symptom duration',
    description: 'How long the presenting symptom has been present.',
    aliases: ['symptom duration', 'duration', 'duration of symptoms', 'onset duration'],
    valueType: 'text',
    evidenceMode: 'keyword',
    requiresNearbyValue: false,
    compatibleTargetTypes: ['FIELD', 'OPTION'],
    compatibleFieldTypes: ['select', 'text', 'number'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'severity', conceptType: CONCEPT_TYPES.MEASUREMENT,
    canonicalLabel: 'Severity',
    description: 'Severity score for the presenting problem (e.g. 0–10).',
    aliases: ['severity', 'severity score', 'pain score', 'pain scale'],
    valueType: 'number',
    evidenceMode: 'evidence_only',
    requiresNearbyValue: false,
    compatibleTargetTypes: ['FIELD'],
    compatibleFieldTypes: ['number', 'severity', 'slider'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'],
  }),

  // ── DATA_ONLY severity / function (L-1) ────────────────────────────────────
  makeConcept({
    conceptId: 'pain_severity', conceptType: CONCEPT_TYPES.MEASUREMENT,
    canonicalLabel: 'Pain severity',
    description: 'Pain intensity/severity score (e.g. 0–10).',
    aliases: ['pain severity', 'pain score', 'pain scale', 'pain level'],
    valueType: 'number',
    evidenceMode: 'evidence_only',
    requiresNearbyValue: false,
    compatibleTargetTypes: ['FIELD'],
    compatibleFieldTypes: ['number', 'severity', 'slider'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'],
  }),
  makeConcept({
    conceptId: 'functional_limitation', conceptType: CONCEPT_TYPES.SYMPTOM,
    canonicalLabel: 'Functional limitation',
    description: 'A functional / activity limitation affecting daily life.',
    aliases: ['functional limitation', 'difficulty walking', 'unable to work', 'limited mobility', 'activity limitation'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATE CONCEPTS — DATA_ONLY (need a DATE recognition group, deferred)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'onset_date', conceptType: CONCEPT_TYPES.DATE_EVENT,
    canonicalLabel: 'Onset date',
    description: 'Date the presenting symptom or event began.',
    aliases: ['onset date', 'date of onset', 'symptom onset', 'onset'],
    requiresNearbyValue: true,
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'test_date', conceptType: CONCEPT_TYPES.DATE_EVENT,
    canonicalLabel: 'Test date',
    description: 'Date a test / sample was performed or collected.',
    aliases: ['test date', 'date of test', 'sample date', 'collected on', 'date collected'],
    requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT', 'IMAGING_REPORT', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'procedure_date', conceptType: CONCEPT_TYPES.DATE_EVENT,
    canonicalLabel: 'Procedure date',
    description: 'Date a procedure / surgery was performed.',
    aliases: ['procedure date', 'date of procedure', 'surgery date', 'date of surgery'],
    requiresNearbyValue: true,
    preferredDocumentTypes: ['CLINICAL_NOTE', 'DISCHARGE_SUMMARY', 'CONSULT_NOTE'],
  }),
  makeConcept({
    conceptId: 'last_seen_date', conceptType: CONCEPT_TYPES.DATE_EVENT,
    canonicalLabel: 'Last seen date',
    description: 'Date the patient was last seen / reviewed.',
    aliases: ['last seen', 'date last seen', 'last visit', 'last reviewed'],
    requiresNearbyValue: true,
    preferredDocumentTypes: ['CLINICAL_NOTE', 'CONSULT_NOTE', 'REFERRAL_LETTER'],
  }),
  makeConcept({
    conceptId: 'diagnosis_date', conceptType: CONCEPT_TYPES.DATE_EVENT,
    canonicalLabel: 'Diagnosis date',
    description: 'Date a diagnosis was made.',
    aliases: ['date of diagnosis', 'diagnosed on', 'diagnosis date'],
    requiresNearbyValue: true,
    preferredDocumentTypes: ['CLINICAL_NOTE', 'CONSULT_NOTE', 'REFERRAL_LETTER'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // FINDING CONCEPTS
  // ══════════════════════════════════════════════════════════════════════════
  // ── LIVE_LINKABLE (L-1) — abnormal_imaging (promoted), abnormal_lab (new),
  //    prior_workup (promoted). Narrow multi-word aliases ONLY (findings get no
  //    match-time negation; negatives are recorded for governance/future use).
  makeConcept({
    conceptId: 'abnormal_imaging', conceptType: CONCEPT_TYPES.FINDING,
    canonicalLabel: 'Abnormal imaging',
    description: 'An abnormal imaging finding (MRI / CT / other).',
    aliases: ['abnormal imaging', 'abnormal MRI', 'abnormal CT', 'imaging abnormality', 'abnormal scan'],
    preferredDocumentTypes: ['IMAGING_REPORT'],
    acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
    negativePhrases: ['normal imaging', 'unremarkable imaging', 'normal MRI', 'normal CT'],
    uncertaintyPhrases: ['possible abnormality'],
  }),
  makeConcept({
    conceptId: 'abnormal_lab', conceptType: CONCEPT_TYPES.FINDING,
    canonicalLabel: 'Abnormal lab result',
    description: 'An abnormal laboratory result (out-of-range / deranged bloods).',
    aliases: ['abnormal lab', 'abnormal labs', 'abnormal laboratory result', 'lab abnormality', 'deranged labs', 'abnormal bloodwork', 'abnormal blood test'],
    preferredDocumentTypes: ['LAB_RESULT'],
    acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE', 'DISCHARGE_SUMMARY'],
    negativePhrases: ['normal labs', 'labs within normal limits', 'unremarkable labs', 'normal bloodwork'],
    uncertaintyPhrases: ['possible abnormality'],
  }),
  makeConcept({
    conceptId: 'prior_workup', conceptType: CONCEPT_TYPES.FINDING,
    canonicalLabel: 'Prior workup',
    description: 'Evidence that prior investigations / workup have been completed.',
    aliases: ['prior workup', 'previous workup', 'prior investigations', 'workup completed', 'prior testing'],
    evidenceMode: 'keyword',
    compatibleTargetTypes: ['OPTION', 'FIELD'],
    compatibleFieldTypes: ['select', 'radio', 'multiselect', 'checkbox', 'text'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE', 'LAB_RESULT'],
  }),

  // ── DATA_ONLY findings (L-1) — prior workup variants + abnormal-result kinds ─
  makeConcept({
    conceptId: 'prior_imaging', conceptType: CONCEPT_TYPES.FINDING,
    canonicalLabel: 'Prior imaging',
    description: 'Evidence that prior imaging was performed.',
    aliases: ['prior imaging', 'previous imaging', 'previous MRI', 'previous CT', 'imaging performed', 'prior scan'],
    preferredDocumentTypes: ['IMAGING_REPORT', 'REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'prior_labs', conceptType: CONCEPT_TYPES.FINDING,
    canonicalLabel: 'Prior labs',
    description: 'Evidence that prior labs / bloodwork were performed.',
    aliases: ['prior labs', 'previous labs', 'previous bloods', 'labs performed', 'prior bloodwork'],
    preferredDocumentTypes: ['LAB_RESULT', 'REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'prior_medication_trial', conceptType: CONCEPT_TYPES.FINDING,
    canonicalLabel: 'Prior medication trial',
    description: 'Evidence of a previous medication trial.',
    aliases: ['prior medication trial', 'medication trial', 'tried medication', 'previous treatment trial', 'failed medication'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE', 'MEDICATION_LIST'],
  }),
  makeConcept({
    conceptId: 'previous_treatment', conceptType: CONCEPT_TYPES.FINDING,
    canonicalLabel: 'Previous treatment',
    description: 'Evidence of previous treatment / therapy.',
    aliases: ['previous treatment', 'prior treatment', 'previously treated', 'prior therapy'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'failed_conservative_management', conceptType: CONCEPT_TYPES.FINDING,
    canonicalLabel: 'Failed conservative management',
    description: 'Evidence that conservative management was tried and failed.',
    aliases: ['failed conservative management', 'conservative treatment failed', 'failed conservative therapy', 'no improvement with conservative'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'prior_specialist_review', conceptType: CONCEPT_TYPES.FINDING,
    canonicalLabel: 'Prior specialist review',
    description: 'Evidence the patient was previously seen by a specialist.',
    aliases: ['prior specialist review', 'previously seen by specialist', 'prior consult', 'seen by specialist'],
    preferredDocumentTypes: ['CONSULT_NOTE', 'REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'abnormal_pathology', conceptType: CONCEPT_TYPES.FINDING,
    canonicalLabel: 'Abnormal pathology',
    description: 'An abnormal pathology / biopsy / histology finding.',
    aliases: ['abnormal pathology', 'abnormal biopsy', 'abnormal histology'],
    preferredDocumentTypes: ['OTHER', 'CLINICAL_NOTE'],
    negativePhrases: ['normal pathology', 'benign biopsy', 'unremarkable histology'],
  }),
  makeConcept({
    conceptId: 'abnormal_ecg', conceptType: CONCEPT_TYPES.FINDING,
    canonicalLabel: 'Abnormal ECG',
    description: 'An abnormal ECG / EKG finding.',
    aliases: ['abnormal ECG', 'abnormal EKG', 'ECG abnormality', 'EKG changes'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'OTHER'],
    negativePhrases: ['normal ECG', 'normal EKG', 'unremarkable ECG'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // MEDICATION / EXPOSURE CONCEPTS — DATA_ONLY (need a MED group, deferred)
  // anticoagulant_use is the highest promote-priority (distinctive drug names).
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'medication_exposure', conceptType: CONCEPT_TYPES.MEDICATION_EXPOSURE,
    canonicalLabel: 'Medication exposure',
    description: 'Evidence the patient is on / exposed to a medication.',
    aliases: ['medication', 'current medication', 'medication exposure', 'on medication', 'taking medication'],
    preferredDocumentTypes: ['MEDICATION_LIST', 'CLINICAL_NOTE', 'REFERRAL_LETTER'],
  }),
  makeConcept({
    conceptId: 'anticoagulant_use', conceptType: CONCEPT_TYPES.MEDICATION_EXPOSURE,
    canonicalLabel: 'Anticoagulant use',
    description: 'Evidence of anticoagulant exposure (warfarin, DOAC, heparin).',
    aliases: ['anticoagulant', 'warfarin', 'apixaban', 'rivaroxaban', 'dabigatran', 'edoxaban', 'DOAC', 'NOAC', 'heparin', 'enoxaparin'],
    preferredDocumentTypes: ['MEDICATION_LIST', 'CLINICAL_NOTE', 'DISCHARGE_SUMMARY'],
    negativePhrases: ['not on anticoagulation', 'no anticoagulant'],
  }),
  makeConcept({
    conceptId: 'antiplatelet_use', conceptType: CONCEPT_TYPES.MEDICATION_EXPOSURE,
    canonicalLabel: 'Antiplatelet use',
    description: 'Evidence of antiplatelet exposure (aspirin, clopidogrel).',
    aliases: ['antiplatelet', 'aspirin', 'clopidogrel', 'ticagrelor', 'prasugrel', 'dipyridamole'],
    preferredDocumentTypes: ['MEDICATION_LIST', 'CLINICAL_NOTE'],
    negativePhrases: ['not on antiplatelet', 'no antiplatelet'],
  }),
  makeConcept({
    conceptId: 'steroid_use', conceptType: CONCEPT_TYPES.MEDICATION_EXPOSURE,
    canonicalLabel: 'Steroid use',
    description: 'Evidence of corticosteroid exposure.',
    aliases: ['steroid', 'corticosteroid', 'prednisone', 'prednisolone', 'dexamethasone', 'hydrocortisone'],
    preferredDocumentTypes: ['MEDICATION_LIST', 'CLINICAL_NOTE'],
    negativePhrases: ['no steroid', 'not on steroids'],
  }),
  makeConcept({
    conceptId: 'immunosuppression', conceptType: CONCEPT_TYPES.MEDICATION_EXPOSURE,
    canonicalLabel: 'Immunosuppression',
    description: 'Evidence of immunosuppressive therapy / state.',
    aliases: ['immunosuppression', 'immunosuppressed', 'methotrexate', 'azathioprine', 'tacrolimus', 'biologic', 'rituximab', 'mycophenolate'],
    preferredDocumentTypes: ['MEDICATION_LIST', 'CLINICAL_NOTE', 'CONSULT_NOTE'],
    negativePhrases: ['immunocompetent', 'not immunosuppressed'],
  }),
  makeConcept({
    conceptId: 'recent_medication_change', conceptType: CONCEPT_TYPES.MEDICATION_EXPOSURE,
    canonicalLabel: 'Recent medication change',
    description: 'Evidence of a recent medication change.',
    aliases: ['recent medication change', 'medication changed', 'new medication', 'dose change', 'recently started'],
    preferredDocumentTypes: ['MEDICATION_LIST', 'CLINICAL_NOTE'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // COMMON CLINICAL-SIGNAL CONCEPTS — DATA_ONLY (need a SYMPTOM group, deferred)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'pregnancy', conceptType: CONCEPT_TYPES.FINDING,
    canonicalLabel: 'Pregnancy',
    description: 'Evidence of current pregnancy.',
    aliases: ['pregnant', 'pregnancy', 'gravid', 'gestation', 'expecting'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'LAB_RESULT'],
    negativePhrases: ['not pregnant', 'negative pregnancy test', 'no pregnancy'],
  }),
  makeConcept({
    conceptId: 'fever', conceptType: CONCEPT_TYPES.SYMPTOM,
    canonicalLabel: 'Fever',
    description: 'Documented fever / febrile state.',
    aliases: ['fever', 'febrile', 'pyrexia', 'high temperature', 'documented fever'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
    negativePhrases: ['afebrile', 'no fever', 'no febrile'],
  }),
  makeConcept({
    conceptId: 'weight_loss', conceptType: CONCEPT_TYPES.SYMPTOM,
    canonicalLabel: 'Weight loss',
    description: 'Unintentional weight loss.',
    aliases: ['weight loss', 'unintentional weight loss', 'losing weight', 'weight loss noted'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'],
    negativePhrases: ['no weight loss', 'stable weight', 'weight stable'],
  }),
  makeConcept({
    conceptId: 'night_sweats', conceptType: CONCEPT_TYPES.SYMPTOM,
    canonicalLabel: 'Night sweats',
    description: 'Nocturnal / drenching sweats.',
    aliases: ['night sweats', 'nocturnal sweats', 'drenching sweats'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'],
    negativePhrases: ['no night sweats'],
  }),
  makeConcept({
    conceptId: 'shortness_of_breath', conceptType: CONCEPT_TYPES.SYMPTOM,
    canonicalLabel: 'Shortness of breath',
    description: 'Dyspnea / breathlessness symptom (non-red-flag).',
    aliases: ['shortness of breath', 'dyspnea', 'dyspnoea', 'breathlessness', 'SOB'],
    abbreviations: ['SOB'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
    negativePhrases: ['no shortness of breath', 'denies dyspnea'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // MUSCULOSKELETAL — shared DATA_ONLY micro-cluster (L-10). Neutral "where / what
  // observed" vocabulary reused by Rheumatology (L-10) AND Orthopedics (L-11) so
  // neither forks these conceptIds. ALL DATA_ONLY: no pattern, no group → inert.
  // Broad symptoms get no match-time negation → never LIVE here. Anatomy alone
  // (knee/hip/spine/wrist/…) is NOT modeled — it lives inside concept phrases only.
  // `arthritis` here is the GENERIC neutral concept; specific inflammatory subtypes
  // (rheumatoid/psoriatic/…) are rheumatology-owned LIVE concepts.
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({ conceptId: 'joint_pain', conceptType: CONCEPT_TYPES.SYMPTOM, canonicalLabel: 'Joint pain', description: 'Joint pain (neutral MSK; data-only).', aliases: ['joint pain', 'painful joint', 'painful joints'], preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'] }),
  makeConcept({ conceptId: 'back_pain', conceptType: CONCEPT_TYPES.SYMPTOM, canonicalLabel: 'Back pain', description: 'Back pain (neutral MSK; data-only).', aliases: ['back pain', 'low back pain', 'lower back pain'], preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'] }),
  makeConcept({ conceptId: 'neck_pain', conceptType: CONCEPT_TYPES.SYMPTOM, canonicalLabel: 'Neck pain', description: 'Neck pain (neutral MSK; data-only).', aliases: ['neck pain', 'cervical pain'], preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'] }),
  makeConcept({ conceptId: 'limb_pain', conceptType: CONCEPT_TYPES.SYMPTOM, canonicalLabel: 'Limb pain', description: 'Limb pain (neutral MSK; data-only).', aliases: ['limb pain', 'leg pain', 'arm pain'], preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'] }),
  makeConcept({ conceptId: 'joint_swelling', conceptType: CONCEPT_TYPES.SYMPTOM, canonicalLabel: 'Joint swelling', description: 'Joint swelling (neutral MSK; data-only).', aliases: ['joint swelling', 'swollen joint', 'swollen joints'], preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'] }),
  makeConcept({ conceptId: 'stiffness', conceptType: CONCEPT_TYPES.SYMPTOM, canonicalLabel: 'Stiffness', description: 'Joint / muscle stiffness (neutral MSK; data-only).', aliases: ['stiffness', 'joint stiffness', 'morning stiffness'], preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'] }),
  makeConcept({ conceptId: 'reduced_range_of_motion', conceptType: CONCEPT_TYPES.FINDING, canonicalLabel: 'Reduced range of motion', description: 'Reduced range of motion (neutral MSK; data-only).', aliases: ['reduced range of motion', 'restricted range of motion', 'limited range of motion'], preferredDocumentTypes: ['CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'reduced_mobility', conceptType: CONCEPT_TYPES.FINDING, canonicalLabel: 'Reduced mobility', description: 'Reduced mobility (neutral MSK; data-only).', aliases: ['reduced mobility', 'limited mobility', 'impaired mobility'], preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'] }),
  makeConcept({ conceptId: 'arthralgia', conceptType: CONCEPT_TYPES.SYMPTOM, canonicalLabel: 'Arthralgia', description: 'Arthralgia (neutral MSK; data-only).', aliases: ['arthralgia', 'joint ache'], preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'] }),
  makeConcept({ conceptId: 'arthritis', conceptType: CONCEPT_TYPES.FINDING, canonicalLabel: 'Arthritis', description: 'Arthritis (generic / unspecified; neutral MSK; data-only — specific inflammatory subtypes are rheumatology-owned).', aliases: ['arthritis'], preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'] }),
  makeConcept({ conceptId: 'joint_effusion', conceptType: CONCEPT_TYPES.FINDING, canonicalLabel: 'Joint effusion', description: 'Joint effusion (neutral MSK; data-only).', aliases: ['joint effusion', 'effusion'], preferredDocumentTypes: ['IMAGING_REPORT', 'CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'musculoskeletal_ultrasound_report', conceptType: CONCEPT_TYPES.DOCUMENT, canonicalLabel: 'Musculoskeletal ultrasound report', description: 'MSK ultrasound report (IMAGING_REPORT; neutral MSK; data-only).', aliases: ['musculoskeletal ultrasound', 'MSK ultrasound', 'joint ultrasound'], preferredDocumentTypes: ['IMAGING_REPORT'], acceptableDocumentTypes: ['CLINICAL_NOTE'] }),

  // ══════════════════════════════════════════════════════════════════════════
  // RED-FLAG CONCEPTS
  // ══════════════════════════════════════════════════════════════════════════
  // ── Existing LIVE red flags (moved verbatim) — RED_FLAG group, positive_only ─
  makeConcept({
    conceptId: 'active_gi_bleeding', conceptType: CONCEPT_TYPES.RED_FLAG,
    canonicalLabel: 'Active GI bleeding',
    description: 'Active gastrointestinal bleeding red flag.',
    positivePhrases: ['active GI bleeding', 'GI bleeding', 'melena', 'hematemesis'],
    negativePhrases: ['denies bleeding', 'no active bleeding', 'no GI bleeding'],
    uncertaintyPhrases: ['rule out bleeding', 'history of bleeding'],
    excludedPhrases: ['old resolved bleeding', 'family history of bleeding'],
    commonFalseMatches: ['bleeding precautions', 'bleeding risk'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'chest_pain', conceptType: CONCEPT_TYPES.RED_FLAG,
    canonicalLabel: 'Chest pain',
    description: 'Active chest pain / angina red flag.',
    positivePhrases: ['chest pain', 'active chest pain', 'angina'],
    negativePhrases: ['denies chest pain', 'no chest pain'],
    uncertaintyPhrases: ['rule out chest pain', 'history of chest pain'],
    excludedPhrases: ['family history of chest pain'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'],
  }),

  // ── DATA_ONLY red flags (L-1) — fully boundaried, NOT recognition-reachable.
  //    No pattern + not in RECOGNITION_GROUPS.RED_FLAG → never auto-links. Ready
  //    to promote after explicit clinical review (one-line pattern/group add).
  makeConcept({
    conceptId: 'active_bleeding', conceptType: CONCEPT_TYPES.RED_FLAG,
    canonicalLabel: 'Active bleeding',
    description: 'Active bleeding / hemorrhage red flag (generalized).',
    positivePhrases: ['active bleeding', 'ongoing bleeding', 'ongoing hemorrhage', 'hemorrhage', 'haemorrhage'],
    negativePhrases: ['no active bleeding', 'bleeding stopped', 'no bleeding'],
    uncertaintyPhrases: ['history of bleeding', 'risk of bleeding'],
    excludedPhrases: ['bleeding risk', 'bleeding precautions', 'family history of bleeding'],
    commonFalseMatches: ['bleeding risk', 'bleeding precautions'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'syncope', conceptType: CONCEPT_TYPES.RED_FLAG,
    canonicalLabel: 'Syncope',
    description: 'Syncope / loss of consciousness red flag.',
    positivePhrases: ['syncope', 'loss of consciousness', 'fainting', 'passed out', 'syncopal episode'],
    negativePhrases: ['no syncope', 'denies syncope'],
    uncertaintyPhrases: ['near-syncope', 'presyncope', 'history of syncope'],
    excludedPhrases: ['family history of syncope'],
    commonFalseMatches: ['presyncope', 'near syncope'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'neurological_deficit', conceptType: CONCEPT_TYPES.RED_FLAG,
    canonicalLabel: 'Neurological deficit',
    description: 'New focal neurological deficit red flag.',
    positivePhrases: ['focal neurological deficit', 'new weakness', 'facial droop', 'slurred speech', 'new neurological deficit'],
    negativePhrases: ['no focal deficit', 'neurologically intact', 'no neurological deficit'],
    uncertaintyPhrases: ['resolved deficit', 'transient deficit'],
    excludedPhrases: ['old deficit', 'long-standing deficit', 'chronic deficit'],
    commonFalseMatches: ['no deficit'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'severe_shortness_of_breath', conceptType: CONCEPT_TYPES.RED_FLAG,
    canonicalLabel: 'Severe shortness of breath',
    description: 'Severe / acute dyspnea / respiratory distress red flag.',
    positivePhrases: ['severe shortness of breath', 'acute dyspnea', 'respiratory distress', 'severe dyspnea'],
    negativePhrases: ['no shortness of breath', 'denies dyspnea'],
    uncertaintyPhrases: ['mild dyspnea', 'exertional dyspnea'],
    excludedPhrases: ['stable chronic dyspnea', 'chronic shortness of breath'],
    commonFalseMatches: ['mild dyspnea'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'DISCHARGE_SUMMARY'],
  }),
  makeConcept({
    conceptId: 'sepsis_or_systemic_infection', conceptType: CONCEPT_TYPES.RED_FLAG,
    canonicalLabel: 'Sepsis / systemic infection',
    description: 'Sepsis / systemic infection red flag.',
    positivePhrases: ['sepsis', 'septic', 'systemic infection', 'SIRS', 'septic shock'],
    negativePhrases: ['no sepsis', 'afebrile', 'no signs of infection'],
    uncertaintyPhrases: ['rule out sepsis', 'possible sepsis', 'query sepsis'],
    excludedPhrases: ['sepsis screen negative'],
    commonFalseMatches: ['sepsis screen', 'sepsis protocol'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'DISCHARGE_SUMMARY', 'REFERRAL_LETTER'],
  }),
  makeConcept({
    conceptId: 'severe_uncontrolled_pain', conceptType: CONCEPT_TYPES.RED_FLAG,
    canonicalLabel: 'Severe uncontrolled pain',
    description: 'Severe / intractable / uncontrolled pain red flag.',
    positivePhrases: ['severe uncontrolled pain', 'intractable pain', 'pain crisis', 'uncontrolled pain'],
    negativePhrases: ['pain controlled', 'no pain', 'mild pain'],
    uncertaintyPhrases: ['moderate pain'],
    excludedPhrases: ['chronic stable pain', 'well-controlled pain'],
    commonFalseMatches: ['pain management', 'pain control'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER'],
  }),
];

// ============================================================================
// DELAYED_RISKY — intentionally NOT added in L-1 (documented for governance).
// Broad single words give no match-time negation for findings/symptoms → unsafe
// as auto-linkable concepts; admissible later only as strictly-bounded red_flags
// or once a SYMPTOM group + narrow phrasing exist. Qualifiers like prior_report /
// latest_report / last_reviewed_date are not document/date types. Narrative
// burden/QoL concepts are too subjective for deterministic v1 matching.
// ============================================================================
export const DELAYED_RISKY_CONCEPT_IDS = Object.freeze([
  'prior_report', 'latest_report', 'last_reviewed_date',
  'symptom_burden', 'functional_decline', 'quality_of_life_impact',
  'abnormal_test_result', 'allergy_history',
  'pain', 'bleeding', 'infection', 'swelling', 'mass_or_lump', 'fatigue', 'trauma',
]);

// ============================================================================
// SHARED RECOGNITION PATTERNS (code-level RegExp, keyed by conceptId).
// Flags 'i' only → stateless RegExp.test. Existing patterns are verbatim; the
// three L-1 LIVE findings are appended. DATA_ONLY concepts deliberately have NO
// pattern here (and no RECOGNITION_GROUPS membership in index.js) → inert.
// ============================================================================
export const recognitionPatterns = {
  // existing shared (verbatim)
  creatinine:           [/creatinine/i],
  complete_blood_count: [/\bcbc\b/i, /complete blood count/i, /\bfbc\b/i, /full blood count/i],

  // ── L-8 shared-labs promotion (LIVE; LAB group membership in index.js) ──
  hba1c:                [/\bhba1c\b/i, /\ba1c\b/i, /glycated h[ae]moglobin/i, /h[ae]moglobin a1c/i],
  tsh:                  [/\btsh\b/i, /thyroid stimulating hormone/i],

  // ── L-10 shared-labs promotion (CRP/ESR; LAB group membership in index.js) ──
  c_reactive_protein:             [/\bcrp\b/i, /c[- ]reactive protein/i],
  erythrocyte_sedimentation_rate: [/\besr\b/i, /erythrocyte sedimentation rate/i],
  active_gi_bleeding:   [/gi bleed/i, /gastrointestinal bleed/i, /\bmelena\b/i, /melaena/i, /h[ae]matemesis/i],
  chest_pain:           [/chest pain/i, /angina/i],

  // ── L-1 LIVE additions (FINDING group) ──
  abnormal_lab:     [/abnormal lab/i, /abnormal laborator/i, /lab(oratory)? abnormalit/i, /deranged (labs?|bloods?)/i, /abnormal bloodwork/i],
  abnormal_imaging: [/abnormal imaging/i, /abnormal (mri|ct|scan|x-?ray|ultrasound)/i, /imaging abnormalit/i],
  prior_workup:     [/prior workup/i, /previous workup/i, /workup (done|complete)/i, /prior investigation/i, /previous investigation/i],
};
